import { AxiosError } from 'axios';
import DomPurify from 'dompurify';
import { clsx } from 'clsx';
import { FunctionComponent, useEffect, useState } from 'react';
import { useConfirmModal } from './useConfirmModal';
import { ButtonVariations } from '../../button/models';
import { BaseModalProps } from '../BaseModal/BaseModal';
import { Modal } from '../Modal';
import { IconButton } from '../../button/IconButton/IconButton';
import { Icon } from '../../icon/Icon';
import styles from './styles.module.scss';
import { Banner } from '../../banner/Banner';
import { Button, ButtonProps } from '../../button/Button';
import { confirmDialogDataIds } from './data-ids';
import { RoundIcon, RoundIconProps } from '../../icon/RoundIcon';
import { TextField, TextFieldProps } from '../../input/TextField/TextField';

export interface ConfirmationBodyExtraParams<D extends object> {
  setExtraParams: (params: D) => void;
  disablePositiveButton: (disable: boolean) => void;
}

export interface ConfirmModalProps<T extends object, D extends object> {
  title: string;
  open?: boolean;
  hideTitle?: boolean;
  titleContainerClassName?: string;
  iconName?: string;
  iconContainerBg?: string;
  iconProps?: Partial<RoundIconProps>;
  showConfirmationSubtext?: boolean;
  showConfirmationInput?: boolean;
  body?: string;
  positiveButtonText?: string;
  positiveButtonVariation?: ButtonVariations;
  positiveButtonClass?: string;
  negativeButtonText?: string;
  negativeButtonClass?: string;
  actionContainerClass?: string;
  onPositiveClick?: (extraParams?: D) => void;
  onPositiveClickCallback?: (extraParams?: D) => Promise<unknown>;
  BodyComponent?: FunctionComponent<T & Partial<ConfirmationBodyExtraParams<D>>>;
  positiveButtonPalette?: string;
  bodyComponentProps?: T;
  onClose: (resp?: unknown | boolean) => void;
  onPositiveClickSuccess?: (res: unknown | boolean) => void;
  modalProps?: Pick<BaseModalProps, 'placement' | 'disableRestoreFocus' | 'styling'>;
  positiveBtnProps?: Partial<ButtonProps>;
  negativeBtnProps?: Partial<ButtonProps>;
  hideActionOnError?: (error: AxiosError) => boolean;
  confirmationMessage?: string;
  confirmationInputProps?: Partial<TextFieldProps>;
  confirmationValidation?: (input: string, affectedEntityName: string) => boolean;
  validateAgainstText?: string;
}

const dataIds = confirmDialogDataIds;

export function ConfirmModal<T extends object, D extends object>({
  open,
  title = 'Alert',
  hideTitle = false,
  titleContainerClassName = '',
  iconName,
  iconContainerBg,
  iconProps,
  positiveButtonVariation,
  body = 'Are you Sure?',
  positiveButtonText = 'Confirm',
  negativeButtonText = 'Cancel',
  onPositiveClickCallback,
  onPositiveClick,
  bodyComponentProps,
  BodyComponent,
  onClose,
  negativeButtonClass,
  positiveButtonClass,
  positiveButtonPalette,
  onPositiveClickSuccess,
  showConfirmationSubtext,
  showConfirmationInput = false,
  modalProps,
  negativeBtnProps = {},
  positiveBtnProps = {},
  confirmationMessage = '',
  confirmationInputProps = {},
  confirmationValidation,
  validateAgainstText = '',
  actionContainerClass = '',
  hideActionOnError
}: ConfirmModalProps<T, D>) {
  const [disablePositiveButton, setDisabledPositiveButton] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState('');
  const [affectedEntityName, setAffectedEntityName] = useState('');
  const {
    processing,
    onErrorMessage,
    setExtraParams,
    onPositiveButtonClick,
    close,
    isActionHiddenOnError
  } = useConfirmModal<D>({
    open,
    onPositiveClick,
    onPositiveClickCallback,
    onPositiveClickSuccess,
    onClose,
    hideActionOnError
  });

  useEffect(() => {
    if (showConfirmationInput && confirmationValidation) {
      setDisabledPositiveButton(
        !confirmationValidation(confirmationInput, affectedEntityName || validateAgainstText)
      );
    }

    return () => {
      setDisabledPositiveButton(false);
    };
  }, [confirmationInput, showConfirmationInput, confirmationValidation]);

  return (
    <Modal
      {...modalProps}
      open={!!open}
      onClose={() => close()}
      styling={{ width: '490px', ...modalProps?.styling }}
    >
      <div className='flex-col px-5 py-5'>
        <div className='d-flex justify-between w-100'>
          <div className={clsx('d-flex flex-1', titleContainerClassName)}>
            {iconName ? (
              <RoundIcon
                dataId={dataIds.icon}
                iconName={iconName}
                containerBg={iconContainerBg}
                containerSize={7}
                isRounded={false}
                className='mr-3'
                {...iconProps}
              />
            ) : null}

            {!hideTitle && (
              <span data-id={dataIds.title} className={clsx('text-subheading-2 text-bold')}>
                {title}
              </span>
            )}
          </div>

          <IconButton
            label='Close'
            className={styles.closeIconCta}
            onClick={() => close()}
            dataId={dataIds.close}
            sx={{ width: 28, height: 28 }}
          >
            <Icon name='close' />
          </IconButton>
        </div>

        <div data-id={dataIds.body} className='mt-4'>
          {BodyComponent ? (
            <BodyComponent
              {...(bodyComponentProps as T)}
              setExtraParams={setExtraParams}
              close={close}
              disablePositiveButton={setDisabledPositiveButton}
              setAffectedEntityName={setAffectedEntityName}
            />
          ) : (
            <span
              className='text-body-1 text-secondary'
              dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(body) }}
            />
          )}
        </div>

        {!!showConfirmationSubtext && (
          <div className='text-center mt-2' data-id={dataIds.confirmationSubText}>
            <span className='text-bold'>{positiveButtonText}</span> if you still want to proceed
          </div>
        )}

        {showConfirmationInput && (!BodyComponent || affectedEntityName) && (
          <div
            className='d-flex flex-col text-secondary text-body-1 mt-3'
            data-id={dataIds.confirmationSubText}
          >
            <span dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(confirmationMessage) }} />
            <TextField
              size={confirmationInputProps?.size || 'small'}
              dataId={dataIds.confirmationInput}
              className={clsx('mt-3 w-50', confirmationInputProps.className)}
              onChange={event => {
                setConfirmationInput(event.target.value);
                confirmationInputProps.onChange?.(event);
              }}
              variant={confirmationInputProps.variant || 'outlined'}
              {...confirmationInputProps}
            />
          </div>
        )}

        {onErrorMessage ? (
          <Banner
            className='mt-5'
            open
            dataId={dataIds.errorBanner}
            color='error'
            icon='error-outline'
          >
            {onErrorMessage}
          </Banner>
        ) : null}

        <div className={clsx('border-top mt-6', styles.actionDivider)} />

        <div className={clsx('center-flex-row justify-end', actionContainerClass)}>
          <Button
            onClick={() => close()}
            className={clsx('mt-5', negativeButtonClass)}
            variation='outline'
            dataId={dataIds.cancel}
            {...negativeBtnProps}
          >
            {isActionHiddenOnError ? 'Close' : negativeButtonText}
          </Button>

          {!isActionHiddenOnError ? (
            <Button
              onClick={onPositiveButtonClick}
              className={clsx(positiveButtonClass, 'ml-3 mt-5')}
              palette={positiveButtonPalette}
              disabled={disablePositiveButton || processing}
              variation={positiveButtonVariation}
              showProgress={processing}
              dataId={dataIds.confirm}
              {...positiveBtnProps}
            >
              {positiveButtonText}
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
